import { SearchX } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { manejarErrorDeGuardado } from "@/components/perfiles/errores";
import { PerfilFormulario } from "@/components/perfiles/PerfilFormulario";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { useActualizarPerfil, usePerfiles } from "@/hooks/usePerfiles";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";

/**
 * Pantalla 12 SA — renombrar un perfil.
 *
 * El API no expone `GET /api/perfiles/{id}`, así que el perfil se toma del
 * listado (la misma query, ya cacheada al venir desde ahí).
 */
export function PerfilEditar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const perfiles = usePerfiles();
  const actualizar = useActualizarPerfil();

  const perfil = perfiles.data?.find((p) => p.id === Number(id));
  /* Sin los cuatro estados (ver `lib/estadoDeConsulta.ts`), con la query
     PAUSADA la pantalla decía que el perfil "puede que lo hayan eliminado
     desde otra sesión" sobre datos que nunca se llegaron a pedir. */
  const estado = estadoDeConsulta(perfiles);

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to="/admin/perfiles">Volver a perfiles</EnlaceVolver>

      <p className="text-muted-foreground max-w-[40rem] text-sm">
        Al renombrar el perfil se actualizará en todos los usuarios que lo tengan asignado.
      </p>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar el perfil"
          error={perfiles.error}
          onReintentar={() => perfiles.refetch()}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="el perfil" />
      ) : estado.cargando ? (
        <div
          role="status"
          aria-label="Cargando el perfil"
          className="bg-card w-full max-w-[30rem] space-y-5 rounded-xl border p-6"
        >
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-40 rounded-md" />
        </div>
      ) : !perfil ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchX />
            </EmptyMedia>
            <EmptyTitle>No encontramos ese perfil</EmptyTitle>
            <EmptyDescription>
              Puede que lo hayan eliminado o renombrado desde otra sesión.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild variant="outline">
              <Link to="/admin/perfiles">Volver a perfiles</Link>
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        /* El `key` REMONTA el formulario al cambiar de perfil: react-hook-form
           solo lee `defaultValues` al montar, así que sin esto pasar de un
           perfil a otro dejaría el campo con el nombre del anterior. */
        <PerfilFormulario
          key={perfil.id}
          nombreInicial={perfil.nombre}
          etiquetaEnvio="Guardar cambios"
          enviando={actualizar.isPending}
          onEnviar={(nombre, { errorDeNombre }) =>
            actualizar.mutate(
              { id: perfil.id, nombre },
              {
                onSuccess: (actualizado) => {
                  toast.success(`Perfil «${actualizado.nombre}» actualizado`);
                  navigate("/admin/perfiles");
                },
                onError: (error) =>
                  manejarErrorDeGuardado(
                    error,
                    errorDeNombre,
                    "No pudimos guardar el perfil. Inténtalo de nuevo.",
                  ),
              },
            )
          }
          onCancelar={() => navigate("/admin/perfiles")}
        />
      )}
    </div>
  );
}
